
import { filter, map, mergeMap, shareReplay} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription, throwError } from 'rxjs';
import { FilterService } from '../../Filter/services/abstract-filter.service';
import { ContentLibraryFolderModel } from '../models/content-library-folder-model';
import { StorageSettingsModel } from '../models/storage-settings.model';
import { SortOrderServiceInterface } from '../../Misc/services/sort-order.service.interface';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { AccountStatusApi } from '../../../api/contentLibrary/models/account-status.api.model';
import { MediaServiceApi } from '../../../api/mediaService/media-service.api';
import { MediaSummaryModel } from '../models/content-library-media-summary.model';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { FilterTypeTextModel } from '../../../Common/Filter/models/filter-type-text.model';
import { ContentLibraryFiltersEnum } from '../enums/content-library-filters.enum';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { ContentLibraryMediaService } from './content-library-media.service';
import { ContentLibraryFolderService } from './content-library-folder.service';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { FilterTypeDateQueryModel } from '../../../Common/Filter/models/filter-type-date-query.model';
import { FilterTypeRangeModel } from '../../../Common/Filter/models/filter-type-range.model';
import { UsersService } from '../../../Core/Users/services/users.service';
import { FilterOptionModel } from '../../../Common/Filter/models/filter-option.model';
import { DefaultFoldersEnum } from '../enums/default-folder.enum';
import { ContentLibrarySortEnum } from '../enums/content-library-sort.enum';
import { UserModel } from '../../../Core/Users/models/user.model';

@Injectable()
export class ContentLibrarySearchService extends FilterService implements SortOrderServiceInterface {

    reloadRequest: Observable<ContentLibraryFolderModel[]>;
    private libraryFoldersSubject: BehaviorSubject<ContentLibraryFolderModel[]> = new BehaviorSubject<ContentLibraryFolderModel[]>([]);
    public libraryFolders$ = this.libraryFoldersSubject.asObservable();
    private mediaItemsSubject: BehaviorSubject<MediaSummaryModel[]> = new BehaviorSubject<MediaSummaryModel[]>([]);
    public mediaItems$ = this.mediaItemsSubject.asObservable();
    private displayMediaItemsSubject: BehaviorSubject<MediaSummaryModel[]> = new BehaviorSubject<MediaSummaryModel[]>([]);
    private displayMediaItems$ = this.displayMediaItemsSubject.asObservable();
    public displayMediaItems$$ = this.displayMediaItems$;
    private subscriptions: Subscription[] = [];
    private defaultFolders = DefaultFoldersEnum;
    protected contentLibrarySortFields$: BehaviorSubject<LocalizedEnum[]> = new BehaviorSubject<LocalizedEnum[]>([]);
    protected sortField: LocalizedEnum = null;
    protected sortField$: BehaviorSubject<LocalizedEnum> = new BehaviorSubject<LocalizedEnum>(this.sortField);

    protected sortOrder: SortOrderEnum = SortOrderEnum.ascending;
    protected sortOrder$: BehaviorSubject<SortOrderEnum> = new BehaviorSubject<SortOrderEnum>(this.sortOrder);

    private refreshUsedStorageSubject: BehaviorSubject<void> = new BehaviorSubject<void>(null);
    public storageSettings$ = this.refreshUsedStorageSubject.pipe(
        mergeMap(() => {
            return this.mediaServiceApi.getAccountStatus().pipe(
                map((apiModel: AccountStatusApi) => new StorageSettingsModel(apiModel))
            );
        }),
        filter(x => !!x),
        shareReplay(1)
    );

    accountUsers: UserModel[] = [];

    sortFields: LocalizedEnum[] = [
        {
            enumValue: ContentLibrarySortEnum.NAME,
            displayString: 'Common.contentLibrary.files.sort.fileName'
        },
        {
            enumValue: ContentLibrarySortEnum.UPLOADED_DATE,
            displayString: 'Common.contentLibrary.files.sort.uploadDate'
        },
        {
            enumValue: ContentLibrarySortEnum.FILE_SIZE,
            displayString: 'Common.contentLibrary.files.sort.fileSize'
        }
    ];

    protected dateQuery: DateQueryModel = DateQueryModel.getAllQuery();
    protected dateQuery$: BehaviorSubject<DateQueryModel> = new BehaviorSubject<DateQueryModel>(this.dateQuery);

    protected nameFilter: FilterTypeTextModel = new FilterTypeTextModel(
        ContentLibraryFiltersEnum.NAME,
        'Common.contentLibrary.files.filter.name',
    );

    protected uploadedDateFilter: FilterTypeDateQueryModel = new FilterTypeDateQueryModel(
        ContentLibraryFiltersEnum.UPLOADED_DATE,
        'Common.contentLibrary.files.filter.uploadedDate',
    );

    protected fileSizeFilter: FilterTypeRangeModel = new FilterTypeRangeModel(
        ContentLibraryFiltersEnum.FILE_SIZE,
        'Common.contentLibrary.files.filter.fileSize',
        0,
        undefined,
        'KB',
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
    );

    protected mimeTypeFilter: FilterTypeCheckboxModel<string> = new FilterTypeCheckboxModel<string>(
        ContentLibraryFiltersEnum.MIME_TYPE,
        'Common.contentLibrary.files.filter.fileType',
    );

    protected uploadedByFilter: FilterTypeCheckboxModel<number> = new FilterTypeCheckboxModel<number>(
        ContentLibraryFiltersEnum.UPLOADED_BY,
        'Common.contentLibrary.files.filter.uploadedBy',
    );

    protected approvedByFilter: FilterTypeCheckboxModel<number> = new FilterTypeCheckboxModel<number>(
        ContentLibraryFiltersEnum.APPROVED_BY,
        'Common.contentLibrary.files.filter.approvedBy',
    );

    protected allFilters: FilterTypeModel[];

    constructor(
        private mediaServiceApi: MediaServiceApi,
        private contentLibraryMediaService: ContentLibraryMediaService,
        private contentLibraryFolderService: ContentLibraryFolderService,
        private usersService: UsersService
    ) {
        super();

        this.subscriptions.push(
            combineLatest([
                this.usersService.accountUsers$,
                this.contentLibraryMediaService.all$
            ]).subscribe(([users, mediaItems]) => {
                this.accountUsers = users;
                this.setMediaItems(mediaItems);
            }),
            this.contentLibraryFolderService.all$.subscribe(libraryFolders => {
                this.setLibraryFolders(libraryFolders);
            }
            ));
        this.contentLibrarySortFields$.next(this.sortFields);
        this.sortField = this.sortFields[0];
        this.sortField$.next(this.sortField);
        this.allFilters = this.getFilterDefinitions();
        this.setFilters(this.allFilters);
    }

    getSortableFields(): Observable<any[]> {
        return this.contentLibrarySortFields$;
    }

    getSortOrder(): Observable<SortOrderEnum> {
        return this.sortOrder$;
    }

    getSortField(): Observable<any> {
        return this.sortField$;
    }

    setSortOrder(order: SortOrderEnum): void {
        if (this.sortOrder !== order) {
            this.sortOrder = order;
            this.handleSort();
            this.sortOrder$.next(order);
            this.refreshResults();
        }
    }

    setSortField(field: any): void {
        if (field !== this.sortField) {
            this.sortField = field;
            this.sortField$.next(this.sortField);
            this.handleSort();
            this.refreshResults();
        }
    }

    refreshResults(): void {
        this.setMediaItems(this.displayMediaItemsSubject.value);
    }

    handleSort(): void {
        switch (this.sortField?.enumValue) {
            case this.sortFields[1].enumValue:
                if (this.sortOrder === SortOrderEnum.ascending) {
                    this.displayMediaItemsSubject.value.sort((a, b) => (a.createdDate > b.createdDate) ? 1 : -1);
                } else {
                    this.displayMediaItemsSubject.value.sort((a, b) => (a.createdDate < b.createdDate) ? 1 : -1);
                }
                break;
            case this.sortFields[2].enumValue:
                if (this.sortOrder === SortOrderEnum.ascending) {
                    this.displayMediaItemsSubject.value.sort((a, b) => (a.size > b.size) ? 1 : -1);
                } else {
                    this.displayMediaItemsSubject.value.sort((a, b) => (a.size < b.size) ? 1 : -1);
                }
                break;
            default:
                if (this.sortOrder === SortOrderEnum.ascending) {
                    this.displayMediaItemsSubject.value.sort((a, b) => (a.name.toLowerCase().localeCompare(b.name.toLowerCase())));
                } else {
                    this.displayMediaItemsSubject.value.sort((a, b) => (b.name.toLowerCase().localeCompare(a.name.toLowerCase())));
                }
        }
    }

    getFilterDefinitions(): FilterTypeModel[] {
        return [
            this.nameFilter,
            this.uploadedDateFilter,
            this.fileSizeFilter,
            this.mimeTypeFilter,
            this.uploadedByFilter,
            this.approvedByFilter
        ];
    }

    setFilters(filters: FilterTypeModel[]): void {
        super.setFilters(filters);
    }

    setDateQuery(dateQuery: DateQueryModel): void {
        this.dateQuery = dateQuery;
        this.dateQuery$.next(this.dateQuery);
    }

    getCurrentMediaItems(): MediaSummaryModel[] {
        return this.displayMediaItemsSubject.value;
    }

    setLibraryFolders(libraryFolders: ContentLibraryFolderModel[]): void {
        this.libraryFoldersSubject.next(libraryFolders);
    }

    setMediaItems(mediaItems: MediaSummaryModel[]): void {
        const items = mediaItems || [];
        this.mediaItemsSubject.next(items);
        this.displayMediaItemsSubject.next(items);
        this.setFilterOptions();
        this.applyFilters();
        this.handleSort();
    }

    setSelectedFolder(folder: ContentLibraryFolderModel): void {

        if (folder.name === this.defaultFolders.UNCATEGORIZED) {
            this.contentLibraryMediaService.all$.pipe(
                map(mediaItems => mediaItems.filter(item => !item.libraryIds || item.libraryIds.length === 0))
            ).subscribe(items => {
                this.setMediaItems(items);
            });
        } else if (folder?.id) {
            this.contentLibraryMediaService.getMediaItemsByFolderId(folder.id).subscribe(items => {
                this.setMediaItems(items);
            });
        } else {
            this.contentLibraryMediaService.all$.subscribe(items => {
                this.setMediaItems(items);
            });
        }
    }

    setFilterOptions(): void {
        if (!this.displayMediaItemsSubject.value) {
            return;
        }
        this.mimeTypeFilter.options = [];
        const mimeTypeMap = new Map();
        for (const mediaItem of this.displayMediaItemsSubject.value) {
            const mimeTypeCount = mimeTypeMap.get(mediaItem.mimeType);
            if (mimeTypeCount) {
                mimeTypeMap.set(mediaItem.mimeType, mimeTypeCount + 1);
            } else {
                mimeTypeMap.set(mediaItem.mimeType, 1);
            }
        }

        this.mimeTypeFilter.options = Array.from(mimeTypeMap.entries()).reduce((acc, [mimeType, count]) => {
            const mainType = mimeType.split('/')[0];
            let mimeGrouping = mainType;
            if (mainType === 'application') {
                mimeGrouping = mimeType;
            }
            const existingOption = acc.find(option => option.label === mimeGrouping);
            if (existingOption) {
                existingOption.count += count;
            } else {
                acc.push({ value: mimeGrouping, count, label: mimeGrouping });
            }
            return acc;
        }, []);

        const uploadedByOptions: FilterOptionModel<number>[] = [];
        const approvedByOptions: FilterOptionModel<number>[] = [];

        this.accountUsers.forEach(user => {
            let createdCount = 0;
            let approvedByCount = 0;
            this.displayMediaItemsSubject.value.forEach(mediaItem => {
                if (user.id === mediaItem.createdByUserId) {
                    createdCount++;
                }
                if (mediaItem.approvedByUserIds.includes(user.id)) {
                    approvedByCount++;
                }
            });

            if (createdCount !== 0) {
                uploadedByOptions.push({
                    label: user.getDisplayName(),
                    value: user.id,
                    count: createdCount
                } as FilterOptionModel<number>);
            }

            if (approvedByCount !== 0) {
                approvedByOptions.push({
                    label: user.getDisplayName(),
                    value: user.id,
                    count: approvedByCount
                } as FilterOptionModel<number>);
            }
        });
        this.uploadedByFilter.options = uploadedByOptions;
        this.uploadedByFilter.options = uploadedByOptions.sort((a, b) => b.count - a.count);
        this.approvedByFilter.options = approvedByOptions;
        this.approvedByFilter.options = approvedByOptions.sort((a, b) => b.count - a.count);
    }

    applyFilters(): void {
        super.applyFilters();
        if (!this.displayMediaItemsSubject.value) {
            return;
        }
        let filteredItems = this.mediaItemsSubject.value.slice();

        // mimetype filter
        const mimeTypeFilterValues = this.mimeTypeFilter.getValue();
        if (mimeTypeFilterValues.length) {
            filteredItems = filteredItems.filter(item => {
                return mimeTypeFilterValues.some(filterValue =>
                    item.mimeType.startsWith(filterValue)
                );
            });
        }

        // name filter
        const nameFilterValues = this.nameFilter.getValue();
        if (nameFilterValues?.length > 0) {
            filteredItems = filteredItems.filter(item => {
                const itemName = item.name.toLowerCase();
                for (const keyword of nameFilterValues) {
                    if (itemName.includes(keyword.toLowerCase())) {
                        return true;
                    }
                }
                return false;
            });
        }

        // uploaded date
        const uploadedDateFilterValues = this.uploadedDateFilter.getValue();
        if (uploadedDateFilterValues) {
            filteredItems = filteredItems.filter(item => {
                const itemDate = new Date(item.createdDate);
                const isMatch = DateQueryModel.isInDateQuery(itemDate, uploadedDateFilterValues);
                return isMatch;
            });
        }

        // file size
        const filesSizeFilterValues = this.fileSizeFilter.getValue();
        if (filesSizeFilterValues.max || filesSizeFilterValues.min) {
            filesSizeFilterValues.min = filesSizeFilterValues.min || 0;
            filteredItems = filteredItems.filter(item => {
                const itemSizeKB = item.size / 1024;
                if (itemSizeKB >= filesSizeFilterValues.min && (!filesSizeFilterValues.max || itemSizeKB <= filesSizeFilterValues.max)) {
                    return true;
                };
                return false;
            });
        }

        // uploaded by
        const uploadedByFilterValues = this.uploadedByFilter.getValue();
        if (uploadedByFilterValues.length) {
            filteredItems = filteredItems.filter(item => {
                for (const userId of uploadedByFilterValues) {
                    if (item.createdByUserId === userId) {
                        return true;
                    }
                }
                return false;
            });
        }

        // approved by
        const approvedByFilterValues = this.approvedByFilter.getValue();
        if (approvedByFilterValues.length) {
            filteredItems = filteredItems.filter(item => {
                for (const userId of approvedByFilterValues) {
                    if (item.approvedByUserIds.includes(userId)) {
                        return true;
                    }
                }
                return false;
            });
        }
        this.displayMediaItemsSubject.next(filteredItems);
    }

    refreshUsedStorage(): void {
        this.refreshUsedStorageSubject.next();
    }
}
